import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountCircleOutlined,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Category as CategoryIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  HelpOutline as HelpOutlineIcon,
  Inventory2Outlined as InventoryIcon,
  Payment as PaymentIcon,
  PaymentsOutlined as ExpensesIcon,
  PointOfSaleOutlined as SalesIcon,
  QuestionAnswer as FAQIcon,
  ReceiptLongOutlined as ReceiptIcon,
  Search as SearchIcon,
  SecurityOutlined as SecurityIcon,
  SettingsOutlined as SettingsIcon,
  CloudUpload as UploadIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink, Navigate, useLocation, useParams } from 'react-router-dom';
import { PageAccentName } from '../theme/pageAccents';
import PageIconTile from '../components/common/PageIconTile';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { FAQItem, getHelpFAQItems } from '../content/faq';
import { getCurrentCapabilities, getHelpContent, getQuickStartContent } from '../content/help';
import { APP_CONFIG } from '../utils/constants';
import { openWhatsApp } from '../utils/whatsapp';
import WhatsAppButton from '../components/common/WhatsAppButton';
import PublicHeader from '../components/public/PublicHeader';
import PublicFooter from '../components/public/PublicFooter';
import { whatsAppBrandSx } from '../theme/interactions';
import './SupportPages.css';

const stripFAQEmphasis = (answer: string) => answer.replace(/\*\*(.+?)\*\*/g, '$1');

const renderFAQAnswer = (answer: string) => answer.split(/(\*\*.+?\*\*)/g).map((part, index) => (
  part.startsWith('**') && part.endsWith('**')
    ? <strong key={index}>{part.slice(2, -2)}</strong>
    : part
));

const faqCategoryOrder: FAQItem['category'][] = [
  'general', 'customers', 'items', 'sales', 'debts', 'expenses', 'security', 'technical',
];

const relatedCategories: Record<string, FAQItem['category'][]> = {
  dashboard: ['general', 'technical'],
  users: ['security'],
  customers: ['customers'],
  items: ['items'],
  inventory: ['items'],
  sales: ['sales'],
  expenses: ['expenses'],
  debts: ['debts'],
};

const guideAccents: Record<string, PageAccentName> = {
  dashboard: 'dashboard',
  users: 'users',
  customers: 'customers',
  items: 'items',
  inventory: 'items',
  sales: 'sales',
  expenses: 'expenses',
  debts: 'debts',
};

const HelpContent: React.FC = () => {
  const { user, isLoading } = useAuth();
  const { language, t } = useLanguage();
  const { guideSlug } = useParams<{ guideSlug: string }>();
  const { hash } = useLocation();
  const [search, setSearch] = useState('');
  const [faqSearch, setFaqSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<FAQItem['category'] | ''>('');
  const [expanded, setExpanded] = useState<string | false>(false);

  const content = getHelpContent(language);
  const quickStart = getQuickStartContent(language);
  const capabilities = getCurrentCapabilities(language);
  const selectedFeature = guideSlug ? content.features.find(feature => feature.id === guideSlug) : undefined;
  const faqItems = getHelpFAQItems(language);
  const normalizedSearch = search.trim().toLocaleLowerCase(language);
  const visibleFeatures = content.features.filter(feature => !normalizedSearch
    || [feature.title, feature.description, ...feature.steps].some(value => value.toLocaleLowerCase(language).includes(normalizedSearch)));
  const normalizedFaqSearch = faqSearch.trim().toLocaleLowerCase(language);
  const matchingFAQs = faqItems.filter(item => !normalizedFaqSearch
    || `${item.question} ${stripFAQEmphasis(item.answer)}`.toLocaleLowerCase(language).includes(normalizedFaqSearch));
  const categories = faqCategoryOrder.filter(category => faqItems.some(item => item.category === category));
  const filteredFAQs = matchingFAQs.filter(item => !selectedCategory || item.category === selectedCategory);
  const visibleCategories = faqCategoryOrder.filter(category => filteredFAQs.some(item => item.category === category));
  const relatedFAQs = selectedFeature
    ? faqItems.filter(item => (relatedCategories[selectedFeature.id] || []).includes(item.category))
    : [];

  useEffect(() => {
    if (hash === '#faq' && !guideSlug) {
      document.getElementById('faq')?.scrollIntoView({ block: 'start' });
    }
  }, [hash, guideSlug]);

  const getCategoryName = (category: FAQItem['category']) => {
    const names: Record<FAQItem['category'], { en: string; tr: string }> = {
      general: { en: 'General', tr: 'Genel' },
      customers: { en: 'Customers', tr: 'Müşteriler' },
      items: { en: 'Items', tr: 'Ürünler' },
      sales: { en: 'Sales', tr: 'Satışlar' },
      debts: { en: 'Debts', tr: 'Borçlar' },
      expenses: { en: 'Expenses', tr: 'Giderler' },
      security: { en: 'Security', tr: 'Güvenlik' },
      technical: { en: 'Technical', tr: 'Teknik' },
    };
    return names[category][language];
  };

  const getCategoryIcon = (category: FAQItem['category']) => {
    switch (category) {
      case 'customers': return <AccountCircleOutlined />;
      case 'items': return <InventoryIcon />;
      case 'sales': return <SalesIcon />;
      case 'debts': return <ReceiptIcon />;
      case 'expenses': return <ExpensesIcon />;
      case 'security': return <SecurityIcon />;
      case 'technical': return <SettingsIcon />;
      default: return <FAQIcon />;
    }
  };

  const contact = language === 'en' ? {
    stillHaveQuestions: 'Need a hand?',
    contactMessage: 'If you cannot find the answer here, our team can help you get back to business.',
    whatsappButton: 'Chat with us on WhatsApp',
    guides: 'Guides',
    guideAction: 'Open guide',
    faqTitle: 'Frequently Asked Questions',
    faqSearchPlaceholder: 'Search questions',
    questions: 'questions',
    noGuides: 'No guides match your search.',
  } : {
    stillHaveQuestions: 'Desteğe mi ihtiyacınız var?',
    contactMessage: 'Aradığınız yanıtı burada bulamadıysanız ekibimiz size yardımcı olmaktan memnuniyet duyar.',
    whatsappButton: 'WhatsApp üzerinden yazın',
    guides: 'Kılavuzlar',
    guideAction: 'Kılavuzu aç',
    faqTitle: 'Sıkça Sorulan Sorular',
    faqSearchPlaceholder: 'Sorularda ara',
    questions: 'soru',
    noGuides: 'Aramanızla eşleşen kılavuz bulunamadı.',
  };

  const handleWhatsAppClick = () => {
    const message = language === 'en'
      ? 'Hello, I need help with the Veresiye application.'
      : 'Merhaba, Veresiye uygulaması hakkında yardıma ihtiyacım var.';
    openWhatsApp({ phoneNumber: APP_CONFIG.WHATSAPP_NUMBER, message });
  };

  const renderStepWithIcons = (step: string) => step.split(/(<[^>]+\/>)/).map((part, index) => {
    if (part.includes('<AddIcon/>')) return <AddIcon key={index} fontSize="small" color="primary" />;
    if (part.includes('<EditIcon/>')) return <EditIcon key={index} fontSize="small" color="primary" />;
    if (part.includes('<SearchIcon/>')) return <SearchIcon key={index} fontSize="small" color="primary" />;
    if (part.includes('<UploadIcon/>')) return <UploadIcon key={index} fontSize="small" color="primary" />;
    if (part.includes('<PaymentIcon/>')) return <PaymentIcon key={index} fontSize="small" color="primary" />;
    if (part.includes('<FilterIcon/>')) return <FilterIcon key={index} fontSize="small" color="primary" />;
    if (part.includes('<CategoryIcon/>')) return <CategoryIcon key={index} fontSize="small" color="primary" />;
    if (part.includes('<SettingsIcon/>')) return <SettingsIcon key={index} fontSize="small" color="primary" />;
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });

  if (guideSlug && !selectedFeature) return <Navigate to="/help" replace />;

  return (
    <Box
      className="support-page support-page-help"
      sx={theme => ({
        minHeight: '100vh',
        bgcolor: 'background.default',
        '--support-page-bg': theme.palette.background.default,
        '--support-panel-bg': theme.palette.background.paper,
        '--support-search-bg': theme.palette.action.hover,
        '--support-search-text': theme.palette.text.primary,
        '--support-search-muted': theme.palette.text.secondary,
        '--support-search-border': theme.palette.divider,
        '--support-search-border-hover': theme.palette.primary.main,
        '--support-faq-category-accent': theme.palette.primary.main,
        '--support-faq-category-on-accent': theme.palette.primary.contrastText,
        '--support-faq-category-surface': theme.palette.primary.main,
        '--support-faq-category-on-surface': theme.palette.primary.contrastText,
        '--support-faq-category-icon-bg': alpha(theme.palette.primary.contrastText, theme.palette.mode === 'dark' ? 0.12 : 0.78),
        '--support-faq-category-count-bg': alpha(theme.palette.primary.contrastText, 0.16),
        '--support-faq-category-count-border': alpha(theme.palette.primary.contrastText, 0.34),
        '--support-faq-category-count-color': theme.palette.primary.contrastText,
      })}
    >
      {!user && <PublicHeader />}

      <Box component="header" className="support-hero support-hero-help">
        <Container maxWidth="lg">
          <Box className="support-hero-copy">
            <Box className="support-hero-kicker">
              <Box className="support-hero-icon"><HelpOutlineIcon /></Box>
              <Typography component="span" variant="overline">{t('nav.help')}</Typography>
            </Box>
            <Typography component="h1" className="support-hero-title">
              {selectedFeature?.title || content.title}
            </Typography>
            <Typography className="support-hero-subtitle">
              {selectedFeature?.description || content.subtitle}
            </Typography>
          </Box>
          <Box className="support-hero-art support-hero-art--help" aria-hidden="true"><span>✓</span></Box>
        </Container>
      </Box>

      <Container maxWidth="lg" className="support-content">
        {selectedFeature ? (
          <>
            <Button component={RouterLink} to="/help" startIcon={<ArrowBackIcon />} className="help-back-link">
              {language === 'en' ? 'All guides and FAQs' : 'Tüm kılavuzlar ve sorular'}
            </Button>
            <Paper className="help-guide-detail" elevation={0}>
              <Box className="help-guide-detail-heading">
                <PageIconTile icon={selectedFeature.icon} accent={guideAccents[selectedFeature.id]} size={50} iconSize={27} onDarkSurface />
                <Box sx={{ minWidth: 0 }}>
                  <Typography component="h2" variant="h5" fontWeight={800}>{selectedFeature.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedFeature.description}</Typography>
                </Box>
              </Box>
              <Typography className="support-step-heading">{content.stepByStep}</Typography>
              <List className="support-step-list">
                {selectedFeature.steps.map((step, index) => (
                  <ListItem key={`${selectedFeature.id}-${index}`}>
                    <span className="support-step-number">{String(index + 1).padStart(2, '0')}</span>
                    <ListItemText primary={(
                      <Typography className="support-step-text" variant="body2">
                        <Box component="span" className="support-step-inline">{renderStepWithIcons(step)}</Box>
                      </Typography>
                    )} />
                  </ListItem>
                ))}
              </List>
            </Paper>

            {relatedFAQs.length > 0 && (
              <Box component="section" className="help-related-faq" aria-labelledby="related-faq-title">
                <Box className="support-section-heading">
                  <Typography id="related-faq-title" component="h2">
                    {language === 'en' ? 'Related questions' : 'İlgili sorular'}
                  </Typography>
                </Box>
                {relatedFAQs.map(item => (
                  <Accordion className="support-accordion faq-accordion" key={item.id} expanded={expanded === item.id}
                    onChange={(_event, isExpanded) => setExpanded(isExpanded ? item.id : false)} disableGutters>
                    <AccordionSummary className="support-accordion-summary" expandIcon={<ExpandMoreIcon />}>
                      <Typography className="support-accordion-title">{item.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails className="support-accordion-details">
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75, '& strong': { color: 'text.primary', fontWeight: 700 } }}>
                        {renderFAQAnswer(item.answer)}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
                <Button component={RouterLink} to="/help#faq" endIcon={<ArrowForwardIcon />} sx={{ mt: 1.5 }}>
                  {language === 'en' ? 'Browse all FAQs' : 'Tüm soruları görüntüle'}
                </Button>
              </Box>
            )}
          </>
        ) : (
          <>
            <Paper className="help-capabilities" elevation={0} component="section" aria-labelledby="capabilities-title">
              <Box className="support-section-heading">
                <Box>
                  <Typography id="capabilities-title" component="h2">{capabilities.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{capabilities.description}</Typography>
                </Box>
              </Box>
              <Box className="help-capabilities-grid">
                {capabilities.items.map(item => (
                  <Box className="help-capability" key={item.title}>
                    <Typography component="h3" variant="subtitle1" fontWeight={800}>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.description}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>

            <Paper className="help-quick-start" elevation={0} component="section" aria-labelledby="quick-start-title">
              <Box className="support-section-heading">
                <Box>
                  <Typography id="quick-start-title" component="h2">{quickStart.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{quickStart.description}</Typography>
                </Box>
              </Box>
              <Box className="help-quick-start-grid">
                {quickStart.steps.map((step, index) => (
                  <Paper component={RouterLink} to={step.href} className="help-quick-start-card" key={step.id} elevation={0}>
                    <span className="support-step-number">{String(index + 1).padStart(2, '0')}</span>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography component="h3" variant="subtitle1" fontWeight={800}>{step.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{step.description}</Typography>
                    </Box>
                    <ArrowForwardIcon fontSize="small" />
                  </Paper>
                ))}
              </Box>
            </Paper>

            <Paper className="support-search-panel" elevation={0}>
              <TextField fullWidth value={search} placeholder={t('help.searchPlaceholder')} aria-label={t('help.searchPlaceholder')}
                onChange={event => setSearch(event.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                  endAdornment: search ? <InputAdornment position="end"><IconButton aria-label={t('common.clearSearch')} onClick={() => setSearch('')} edge="end"><ClearIcon /></IconButton></InputAdornment> : undefined,
                }} />
            </Paper>

            <Box component="section" aria-labelledby="help-guides-title">
              <Box className="support-section-heading support-section-heading--count">
                <Typography id="help-guides-title" component="h2">{contact.guides}</Typography>
                <Chip className="support-count-chip support-count-chip--accent" size="small" label={visibleFeatures.length}
                  aria-label={`${visibleFeatures.length} ${t('help.topicCount')}`} />
              </Box>
              {visibleFeatures.length > 0 ? (
                <Box className="help-guide-grid">
                  {visibleFeatures.map(feature => (
                    <Paper component={RouterLink} to={`/help/${feature.id}`} className="help-guide-card" key={feature.id} elevation={0}>
                      <PageIconTile icon={feature.icon} accent={guideAccents[feature.id]} size={46} iconSize={24} onDarkSurface />
                      <Box className="help-guide-card-copy">
                        <Typography component="h3" className="help-guide-card-title">{feature.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{feature.description}</Typography>
                        <Typography className="help-guide-card-action">{contact.guideAction}</Typography>
                      </Box>
                      <ArrowForwardIcon className="help-guide-card-arrow" fontSize="small" />
                    </Paper>
                  ))}
                </Box>
              ) : <Paper className="support-empty-state" elevation={0}>{contact.noGuides}</Paper>}
            </Box>

            <Box component="section" id="faq" className="help-faq-section" aria-labelledby="help-faq-title">
              <Box className="support-section-heading support-section-heading--count">
                <Typography id="help-faq-title" component="h2">{contact.faqTitle}</Typography>
                <Chip className="support-count-chip support-count-chip--accent" size="small" label={filteredFAQs.length}
                  aria-label={`${filteredFAQs.length} ${contact.questions}`} />
              </Box>
              <Paper className="help-faq-filter" elevation={0}>
                <TextField fullWidth size="small" value={faqSearch} placeholder={contact.faqSearchPlaceholder} aria-label={contact.faqSearchPlaceholder}
                  onChange={event => setFaqSearch(event.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                    endAdornment: faqSearch ? <InputAdornment position="end"><IconButton aria-label={t('common.clearSearch')} onClick={() => setFaqSearch('')} edge="end"><ClearIcon /></IconButton></InputAdornment> : undefined,
                  }} />
                <Typography className="support-topic-label">{t('faq.browseTopics')}</Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip className="support-topic-chip" label={t('faq.allTopics')} clickable variant={!selectedCategory ? 'filled' : 'outlined'} onClick={() => setSelectedCategory('')} />
                  {categories.map(category => (
                    <Chip className="support-topic-chip" key={category} label={getCategoryName(category)} clickable
                      variant={selectedCategory === category ? 'filled' : 'outlined'}
                      onClick={() => setSelectedCategory(selectedCategory === category ? '' : category)} />
                  ))}
                </Stack>
              </Paper>

              {filteredFAQs.length > 0 ? (
                <Box className="faq-category-grid">
                  {visibleCategories.map((category, categoryIndex) => {
                    const categoryFAQs = filteredFAQs.filter(item => item.category === category);
                    return (
                      <Box className="faq-category-group" key={category} style={{ animationDelay: `${categoryIndex * 55}ms` }}>
                        <Paper className="faq-category-heading" elevation={0}>
                          <Box className="faq-category-heading-icon">{getCategoryIcon(category)}</Box>
                          <Box className="faq-category-heading-copy"><Typography variant="subtitle1" fontWeight={800}>{getCategoryName(category)}</Typography></Box>
                          <Chip className="support-count-chip support-count-chip--accent" size="small" label={`${categoryFAQs.length} ${contact.questions}`} />
                        </Paper>
                        {categoryFAQs.map(item => (
                          <Accordion className="support-accordion faq-accordion" key={item.id} expanded={expanded === item.id}
                            onChange={(_event, isExpanded) => setExpanded(isExpanded ? item.id : false)} disableGutters>
                            <AccordionSummary className="support-accordion-summary" expandIcon={<ExpandMoreIcon />}>
                              <Typography className="support-accordion-title">{item.question}</Typography>
                            </AccordionSummary>
                            <AccordionDetails className="support-accordion-details">
                              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75, '& strong': { color: 'text.primary', fontWeight: 700 } }}>
                                {renderFAQAnswer(item.answer)}
                              </Typography>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    );
                  })}
                </Box>
              ) : <Paper className="support-empty-state" elevation={0}>{t('faq.noResults')}</Paper>}

              <Box className="support-contact-panel" aria-labelledby="help-contact-title">
                <Box className="support-contact-copy">
                  <Typography className="support-contact-kicker">{t('landing.contactLabel')}</Typography>
                  <Typography id="help-contact-title" component="h3" variant="h5" className="support-contact-title">{contact.stillHaveQuestions}</Typography>
                  <Typography className="support-contact-description">{contact.contactMessage}</Typography>
                </Box>
                <Button variant="contained" color="success" startIcon={<WhatsAppIcon />} onClick={handleWhatsAppClick}
                  className="support-contact-button" sx={whatsAppBrandSx}>
                  {contact.whatsappButton}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Container>

      {!user && <WhatsAppButton />}
      <PublicFooter showAuthLinks={!user && !isLoading} />
    </Box>
  );
};

export default HelpContent;
